<script setup>
import { ref, watch, inject } from 'vue'
import FilterSearch from "@/components/explorer/filter/FilterSearch.vue"

//events raised by component
const emit = defineEmits([
	'closeFilterItemPanel',
	'updateFilterValue'
])
const { userInputState } = inject('userInputState')

//componenet methods exposed to parent
defineExpose({
    initFilterItem
});

//binding used to invoke method on child component
const childRefFilterSearch = ref({})

//reactive field proxies
let  dimValues = ref()
let  dimName = ref()
let chartType = ref()
//const isDisabled = ref(false)

//local variables
let dim = {}; // ref({})

//methods
function initFilterItem(type, d) {
	//exclude values for shared groups denoted by ';'
	dimValues.value = d.appliedValues.filter(v => !v.name.includes(';'))
	//dimValues.value = d.appliedValues
	dimName.value = d.columnName
	dim = d
	chartType.value = type

	childRefFilterSearch.value.initSearch()
}


function closeFilter(type) {
	emit('closeFilterItemPanel', type);
}

function updateFilter(filterValue) {
	//if this is a geography column:
	if(dim.geoLabelFlag) {
		userInputState.value.geo = true;
	}
	if(import.meta.env.VITE_APP_DEBUG) {
		console.log('updateFilter')
		console.log('userInputState.value.geo',userInputState.value.geo)
	}
	//special case: 'All' is only available as standalone filter for treemap and sankey:
	if(chartType.value == 'treemap' || chartType.value == 'sankey') {
		const allVal = dim.totalLabel
		if(dim.distinctValues.includes(allVal)) {
			if(filterValue.name == allVal) { //user clicking "All" (either selecting or deselecting)
				dim.appliedValues.forEach(i => {
					if(i.name == allVal) {		
						i.selected = filterValue.selected
					} else {
						i.selected = filterValue.selected?false:true;
					}
				})
			} else { //user clicking something other than "All" (either selecting or deselecting)
				dim.appliedValues.forEach(i => {
					if(filterValue.selected && i.name == allVal) {		
						i.selected = false
					}
				})
			}
		}
	}
	//sync up shared groups with displayed group - required for API
	//Parent dim in hierarchy
	if(dim.childColumnName != null) {
		//if dim has a child relationship then it is a parent like Region, when region values are rendered as checkboxes
		//then region must be series. Logic to handle shared group EU-27 
		//TODO: Change logic to not use hard coded EU-27 test	
		let sharedValues = dim.appliedValues.filter(x => x.sharedGrpFullValue)
		if(sharedValues.length > 0) {
			sharedValues.forEach(y => {
				const val = dim.appliedValues.find(z => z.name == y.sharedGrpFullValue)
				if(y.name == 'EU-27')
					val.selected = y.selected;
			})
		}
	}
	emit('updateFilterValue', dim);
}

const shouldDisable = (checkboxId) => {
		//if(dimValues.value.filter(x => x.selected).length < 2)
	const selected = dim.appliedValues.filter(x => x.selected)
	return selected.length === 1 && selected[0].name === checkboxId
};

function resetAll() {
	//if this is a geography column:
	if(dim.geoLabelFlag) {
		userInputState.value.geo = false;
	}
	if(import.meta.env.VITE_APP_DEBUG) {
		console.log('updateFilter')
		console.log('userInputState.value.geo',userInputState.value.geo)
	}
	let allVal = dim.totalLabel
	let totalLabelFound = dim.distinctValues.includes(allVal)
	dim.appliedValues.forEach(i => {
		if(totalLabelFound && i.name == allVal) {		
			i.selected = false;
		} else {
			i.selected = true;
		}
	})
	emit('updateFilterValue', dim);	
}

function areAllValuesSelected() {
	if(dim.appliedValues == null)
		return false
  	return dim.appliedValues.every(value =>
    	value.selected == true
  	)
}

function toggleAllValues() {
	if (areAllValuesSelected()) {
		dim.selected = false
		dim.appliedValues.forEach(value =>
			value.selected = false
		)
	} else {
		dim.selected = true
		dim.appliedValues.forEach(value =>
			value.selected = true
		)
	}
	emit('updateFilterValue', dim);
	return
}

function filteredValues(matchedValues) {
	if(matchedValues == null) {
		dimValues.value = dim.appliedValues
	} else {
		dimValues.value = matchedValues
	}
}
</script>

<template>
	<div class="filter-panel">
		<div class="filter-header">
			<button class="btn-icon btn-arrow" @click="closeFilter" aria-label="Back"><i class="fa-solid fa-chevron-left"></i> Back</button>
			<button class="btn-icon btn-close" @click="closeFilter('all')" aria-label="Close"><i class="fa-solid fa-times"></i></button>
		</div>
		<div class="filter-title">{{dimName}}</div>
		
		<div class="filter-body">
			<div class="filter-body-content">
				<FilterSearch ref="childRefFilterSearch" :dim="dim" @filteredValues="filteredValues" />	
				<div class="filters">
					<ul>
						<li v-for="(dimVal, index) in dimValues" :key="dimVal.name">
							<input type="checkbox" v-bind:id="index" v-model="dimVal.selected" :disabled="shouldDisable(dimVal.name)"  @change="updateFilter(dimVal)">
							<label class="fld-value" v-bind:for="index">{{ dimVal.name }}</label>
						</li>
					</ul>			
				</div>
				<div class="button-section">
					<button class="btn-secondary"
							@click="toggleAllValues()">
						<span v-if="areAllValuesSelected()">
							<i class="fa-solid fa-check"></i> Unselect all
						</span>
						<span v-else>
							<i class="fa-solid fa-check"></i> Select all
						</span>
					</button>			
					<button class="btn-primary" @click="resetAll"> <i class="fa-solid fa-trash-can-undo"></i> Reset All </button>
				</div>
			</div>
		</div>		
	</div>
</template>

<style scoped>
.filter-panel {
	background-color: var(--surface-color-Default);
	display: flex;
	width: 25rem; /* 400px; */ 
	flex-direction: column;
	align-items: flex-start;
	flex-direction: column;
	align-items: flex-start;
	gap: var(--spacing-xl, 1.5rem);
	flex-shrink: 0;
	padding: .75rem 1.25rem;
	border-radius: var(--ui-cards-default, 0.75rem);
	background: var(--surface-default, #FFF);
	height: 100%;
	/* UI Card */
	box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.15);	

	position:absolute;
	top:0;
	left:0;

}
.filter-header {
	display: flex;
	width: 100%; /* 394px; */
	height: 2.5rem;
	justify-content: space-between;
}
.filter-body {
	width: 100%;
	height: 100%;
	min-height: 0;
}
.filter-body-content {
	width: 100%;
	display: flex;
  	flex-direction: column;
  	height: 100%;
}
.filter-title {
	color: var(--text-Header, #171717);

	/* Medium/H6 */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-H6, 1.25rem);
	font-style: normal;
	font-weight: 500;
	line-height: var(--font-line-height-H6, 1.75rem); /* 140% */
}

.btn-close {
	color: var(--icon-cta-Blue, #064291);
	text-align: right;
	font-weight: 900;
	line-height: normal;
	font-size: var(--font-size-H6);
	border: 0;
	background-color: transparent;
	min-width: 1rem;
}

.btn-arrow {
	min-width:0;
	color: var(--icon-cta-Blue, #064291);
	font-size: var(--font-size-Base);
	text-decoration: none;		
}

.filters {
	display: flex;
	padding-inline-start: 0;
	flex-direction: column;
	align-items: flex-start;
	align-self: stretch;
	margin-top: 2rem;
	list-style-type: none;
	/*min-height: 32.625rem;*/
	/*max-height: 34rem;*/
	overflow-y: auto;
	overflow-x: hidden;
}

ul {
	list-style-type: none;
    padding-left: 0;
    width: 100%;	
}

li {
	display: flex;
	align-items: center;
	align-self: stretch;
	height: 3rem;
	width: 100%;
}

.fld-value {
	padding-left: .25rem;
	color: var(--text-primary, #2C2C2C);
	width: 100%;
	/* Regular/Base */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 1.5rem); /* 150% */		
}

input[type='checkbox'] {
	height:1.5rem;
	width:1.5rem;
}

input[type='checkbox']:hover {
	cursor: pointer;
}

.button-section {
	display: flex;
	justify-content:space-between;
	width: 100%;
	border-top: 1px solid var(--border-color-Divider);
	padding-top: 1rem;
	margin-top: auto;
}

/* 1023px - iPad */
@media (max-width: 63.9375rem) {
	.filter-panel {
		position:absolute;
		left: 0;
		top: 0;
		width: 100%;
		min-height: unset;
	}

	.filters {
		min-height: unset;
	}

	.filter-body {
		max-width: 31.1875rem;
		width: 100%;
	}	
}
</style>